import { useState, useRef, useEffect } from 'react'
import {
  Building2,
  Clock,
  DoorOpen,
  Check,
  ChevronRight,
  ChevronLeft,
  Plus,
  Trash2,
  Upload,
  ImagePlus,
  X,
  Globe,
} from 'lucide-react'
import { api, type Building, type Currency } from '../lib/api'
import { AddressLookup, type AddressData } from './AddressLookup'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

interface SetupWizardProps {
  onComplete: () => void
}

type WizardStep = 'building' | 'cleaning' | 'room'

interface CleaningScheduleInput {
  id: string
  name: string
  dayOfWeek: number
  startTime: string
  endTime: string
}

export function SetupWizard({ onComplete }: SetupWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('building')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Currency state
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [selectedCurrency, setSelectedCurrency] = useState('GBP') // Default to GBP for UK

  // Building state - using AddressData for all address fields
  const [buildingAddress, setBuildingAddress] = useState<AddressData>({
    name: '',
    address: '',
    city: '',
    postcode: '',
    country: '',
    googleMapsUrl: '',
  })
  const [buildingDirections, setBuildingDirections] = useState('')
  const [buildingOpeningHours, setBuildingOpeningHours] = useState<
    Record<number, { isActive: boolean; openTime: string; closeTime: string }>
  >(() => {
    // Initialize with Mon-Fri 9-5 defaults
    const hours: Record<number, { isActive: boolean; openTime: string; closeTime: string }> = {}
    for (let i = 0; i < 7; i++) {
      hours[i] = {
        isActive: i >= 1 && i <= 5, // Mon-Fri
        openTime: '09:00',
        closeTime: '17:00',
      }
    }
    return hours
  })
  const [createdBuilding, setCreatedBuilding] = useState<Building | null>(null)

  // Cleaning schedules state
  const [cleaningSchedules, setCleaningSchedules] = useState<CleaningScheduleInput[]>([])

  // Room state
  const [roomName, setRoomName] = useState('')
  const [roomCapacity, setRoomCapacity] = useState('10')
  const [roomHourlyRate, setRoomHourlyRate] = useState('0')
  const [roomDescription, setRoomDescription] = useState('')
  const [roomHeroImage, setRoomHeroImage] = useState<File | null>(null)
  const [heroImagePreview, setHeroImagePreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const steps: { key: WizardStep; label: string; icon: typeof Building2 }[] = [
    { key: 'building', label: 'Add Building', icon: Building2 },
    { key: 'cleaning', label: 'Cleaning Schedule', icon: Clock },
    { key: 'room', label: 'Add Room', icon: DoorOpen },
  ]

  const currentStepIndex = steps.findIndex((s) => s.key === currentStep)

  // Load currencies on mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const data = await api.settings.getCurrencies()
        setCurrencies(data.currencies)
      } catch (err) {
        // Default to common currencies if load fails
        setCurrencies([
          { code: 'GBP', name: 'British Pound', symbol: '£' },
          { code: 'USD', name: 'US Dollar', symbol: '$' },
          { code: 'EUR', name: 'Euro', symbol: '€' },
        ])
      }
    }
    loadCurrencies()
  }, [])

  const handleCreateBuilding = async () => {
    if (!buildingAddress.name.trim()) {
      setError('Building name is required')
      return
    }

    setError('')
    setSaving(true)

    try {
      // First, save the currency setting
      await api.settings.update({
        settings: { currency: selectedCurrency },
      })

      // Then create the building
      const result = await api.buildings.create({
        name: buildingAddress.name,
        address: buildingAddress.address || undefined,
        city: buildingAddress.city || undefined,
        postcode: buildingAddress.postcode || undefined,
        country: buildingAddress.country || undefined,
        googleMapsUrl: buildingAddress.googleMapsUrl || undefined,
        directions: buildingDirections || undefined,
      })

      // Save opening hours for the building
      const scheduleData = Object.entries(buildingOpeningHours).map(([day, data]) => ({
        dayOfWeek: parseInt(day),
        openTime: data.openTime,
        closeTime: data.closeTime,
        isActive: data.isActive,
      }))
      await api.buildings.updateOpeningSchedules(result.building.id, scheduleData)

      setCreatedBuilding(result.building)
      setCurrentStep('cleaning')
    } catch (err: any) {
      setError(err.message || 'Failed to create building')
    } finally {
      setSaving(false)
    }
  }

  const handleAddCleaningSchedule = () => {
    setCleaningSchedules([
      ...cleaningSchedules,
      {
        id: `temp-${Date.now()}`,
        name: 'Cleaning',
        dayOfWeek: 1, // Monday
        startTime: '08:00',
        endTime: '09:00',
      },
    ])
  }

  const handleRemoveCleaningSchedule = (id: string) => {
    setCleaningSchedules(cleaningSchedules.filter((s) => s.id !== id))
  }

  const handleUpdateCleaningSchedule = (
    id: string,
    field: keyof CleaningScheduleInput,
    value: string | number
  ) => {
    setCleaningSchedules(cleaningSchedules.map((s) => (s.id === id ? { ...s, [field]: value } : s)))
  }

  const handleSaveCleaningSchedules = async () => {
    if (!createdBuilding) return

    setError('')
    setSaving(true)

    try {
      // Save all cleaning schedules
      for (const schedule of cleaningSchedules) {
        await api.buildings.createCleaningSchedule(createdBuilding.id, {
          name: schedule.name,
          dayOfWeek: schedule.dayOfWeek,
          startTime: schedule.startTime,
          endTime: schedule.endTime,
        })
      }
      setCurrentStep('room')
    } catch (err: any) {
      setError(err.message || 'Failed to save cleaning schedules')
    } finally {
      setSaving(false)
    }
  }

  const handleCreateRoom = async () => {
    if (!roomName.trim()) {
      setError('Room name is required')
      return
    }

    if (!createdBuilding) {
      setError('No building created')
      return
    }

    setError('')
    setSaving(true)

    try {
      // Create the room first
      const result = await api.rooms.create({
        name: roomName,
        buildingId: createdBuilding.id,
        capacity: parseInt(roomCapacity) || 10,
        hourlyRate: parseFloat(roomHourlyRate) || 0,
        descriptionHtml: roomDescription || undefined,
      })

      // If we have a hero image, upload it
      if (roomHeroImage && result.room?.id) {
        try {
          await api.rooms.uploadImages(result.room.id, [roomHeroImage])
        } catch (imgErr) {
          // Don't fail the whole process if image upload fails
          console.error('Failed to upload hero image:', imgErr)
        }
      }

      onComplete()
    } catch (err: any) {
      setError(err.message || 'Failed to create room')
    } finally {
      setSaving(false)
    }
  }

  const handleHeroImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setRoomHeroImage(file)
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file)
      setHeroImagePreview(previewUrl)
    }
  }

  const handleRemoveHeroImage = () => {
    setRoomHeroImage(null)
    if (heroImagePreview) {
      URL.revokeObjectURL(heroImagePreview)
      setHeroImagePreview(null)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSkipCleaning = () => {
    setCurrentStep('room')
  }

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
      <div className="min-h-full flex items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl border border-border w-full max-w-2xl overflow-hidden">
          {/* Header */}
          <div className="border-b border-border px-6 sm:px-8 py-6 text-gray-900">
            <img src="/roomsi-icon.png" alt="" width={40} height={40} className="mb-4" />
            <h1 className="text-2xl font-bold mb-2">Welcome to Roomsi</h1>
            <p className="text-gray-600">Let's get your rooms ready for guests.</p>
          </div>

          {/* Progress Steps */}
          <div className="px-8 py-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon
                const isActive = step.key === currentStep
                const isCompleted = index < currentStepIndex

                return (
                  <div key={step.key} className="flex items-center">
                    <div
                      className={`flex items-center gap-3 ${
                        isActive
                          ? 'text-primary-600'
                          : isCompleted
                            ? 'text-green-600'
                            : 'text-gray-400'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isActive ? 'bg-primary-100' : isCompleted ? 'bg-green-100' : 'bg-gray-100'
                        }`}
                      >
                        {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                      </div>
                      <span
                        className={`font-medium hidden sm:block ${
                          isActive
                            ? 'text-primary-700'
                            : isCompleted
                              ? 'text-green-700'
                              : 'text-gray-500'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <ChevronRight className="w-5 h-5 text-gray-300 mx-4" />
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-6">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {currentStep === 'building' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    Add Your First Building
                  </h2>
                  <p className="text-sm text-gray-500">
                    Start by entering your postcode to find your location
                  </p>
                </div>

                {/* Currency Selection */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Globe className="w-5 h-5 text-gray-500" />
                    <label className="text-sm font-medium text-gray-700">Currency</label>
                  </div>
                  <select
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                  >
                    {currencies.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.symbol} {c.name} ({c.code})
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-gray-500">
                    This will be used for all room pricing. You can change it later in Settings.
                  </p>
                </div>

                <AddressLookup
                  value={buildingAddress}
                  onChange={setBuildingAddress}
                  buildingNameLabel="Building Name"
                  buildingNamePlaceholder="e.g. Main Office, Community Center, The Hub"
                />

                {buildingAddress.city && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Directions / How to find us{' '}
                        <span className="text-muted-foreground font-normal">(optional)</span>
                      </label>
                      <textarea
                        value={buildingDirections}
                        onChange={(e) => setBuildingDirections(e.target.value)}
                        placeholder="e.g. Enter through the main lobby, take the elevator to floor 2..."
                        rows={2}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    {/* Opening Hours */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center gap-3 mb-3">
                        <Clock className="w-5 h-5 text-gray-500" />
                        <label className="text-sm font-medium text-gray-700">Opening Hours</label>
                      </div>
                      <div className="space-y-2">
                        {DAYS.map((day, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <label className="flex items-center gap-2 w-28">
                              <input
                                type="checkbox"
                                checked={buildingOpeningHours[i]?.isActive ?? false}
                                onChange={(e) =>
                                  setBuildingOpeningHours((prev) => ({
                                    ...prev,
                                    [i]: { ...prev[i], isActive: e.target.checked },
                                  }))
                                }
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-700">{day.slice(0, 3)}</span>
                            </label>

                            {buildingOpeningHours[i]?.isActive ? (
                              <div className="flex items-center gap-2 flex-1">
                                <input
                                  type="time"
                                  value={buildingOpeningHours[i]?.openTime ?? '09:00'}
                                  onChange={(e) =>
                                    setBuildingOpeningHours((prev) => ({
                                      ...prev,
                                      [i]: { ...prev[i], openTime: e.target.value },
                                    }))
                                  }
                                  className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                                />
                                <span className="text-gray-500 text-sm">to</span>
                                <input
                                  type="time"
                                  value={buildingOpeningHours[i]?.closeTime ?? '17:00'}
                                  onChange={(e) =>
                                    setBuildingOpeningHours((prev) => ({
                                      ...prev,
                                      [i]: { ...prev[i], closeTime: e.target.value },
                                    }))
                                  }
                                  className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">Closed</span>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="mt-3 text-xs text-gray-500">
                        Rooms in this building will inherit these hours by default.
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}

            {currentStep === 'cleaning' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Cleaning Schedule</h2>
                  <p className="text-sm text-gray-500">
                    Set up regular cleaning times when rooms will be unavailable for booking
                  </p>
                </div>

                {cleaningSchedules.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                    <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">No cleaning schedules added yet</p>
                    <button
                      onClick={handleAddCleaningSchedule}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
                    >
                      <Plus className="w-4 h-4" />
                      Add Cleaning Time
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cleaningSchedules.map((schedule) => (
                      <div
                        key={schedule.id}
                        className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1 grid grid-cols-4 gap-3">
                          <input
                            type="text"
                            value={schedule.name}
                            onChange={(e) =>
                              handleUpdateCleaningSchedule(schedule.id, 'name', e.target.value)
                            }
                            placeholder="Name"
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                          <select
                            value={schedule.dayOfWeek}
                            onChange={(e) =>
                              handleUpdateCleaningSchedule(
                                schedule.id,
                                'dayOfWeek',
                                parseInt(e.target.value)
                              )
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            {DAYS.map((day, idx) => (
                              <option key={day} value={idx}>
                                {day}
                              </option>
                            ))}
                          </select>
                          <input
                            type="time"
                            value={schedule.startTime}
                            onChange={(e) =>
                              handleUpdateCleaningSchedule(schedule.id, 'startTime', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                          <input
                            type="time"
                            value={schedule.endTime}
                            onChange={(e) =>
                              handleUpdateCleaningSchedule(schedule.id, 'endTime', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveCleaningSchedule(schedule.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    <button
                      onClick={handleAddCleaningSchedule}
                      className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-primary-300 hover:text-primary-600 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Another
                    </button>
                  </div>
                )}
              </div>
            )}

            {currentStep === 'room' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Add Your First Room</h2>
                  <p className="text-sm text-gray-500">
                    Create a bookable space in {createdBuilding?.name || 'your building'}
                  </p>
                </div>

                {/* Hero Image Upload - Prominent Position */}
                <div className="bg-gray-50 rounded-md border border-border p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 p-2 bg-primary-100 rounded-lg">
                      <ImagePlus className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Hero Image{' '}
                        <span className="text-xs text-gray-500 font-normal">
                          (optional but recommended)
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        A great photo makes your room stand out in listings
                      </p>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleHeroImageChange}
                        className="hidden"
                      />

                      {heroImagePreview ? (
                        <div className="relative inline-block">
                          <img
                            src={heroImagePreview}
                            alt="Hero image preview"
                            className="w-40 h-24 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={handleRemoveHeroImage}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:border-primary-300 transition-colors"
                        >
                          <Upload className="w-4 h-4" />
                          Choose Photo
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Room Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={roomName}
                      onChange={(e) => setRoomName(e.target.value)}
                      placeholder="e.g., Conference Room A, Meeting Room 1"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      autoFocus
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Capacity
                      </label>
                      <input
                        type="number"
                        value={roomCapacity}
                        onChange={(e) => setRoomCapacity(e.target.value)}
                        placeholder="10"
                        min="1"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hourly Rate ($)
                      </label>
                      <input
                        type="number"
                        value={roomHourlyRate}
                        onChange={(e) => setRoomHourlyRate(e.target.value)}
                        placeholder="0"
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (optional)
                    </label>
                    <textarea
                      value={roomDescription}
                      onChange={(e) => setRoomDescription(e.target.value)}
                      placeholder="Describe the room, its features, equipment available..."
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
                    <strong>Tip:</strong> You can add more rooms, images, and configure availability
                    schedules later in the Rooms section.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            <div>
              {currentStep !== 'building' && (
                <button
                  onClick={() => setCurrentStep(currentStep === 'room' ? 'cleaning' : 'building')}
                  className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800"
                  disabled={saving}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {currentStep === 'cleaning' && (
                <button
                  onClick={handleSkipCleaning}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  disabled={saving}
                >
                  Skip for now
                </button>
              )}

              <button
                onClick={
                  currentStep === 'building'
                    ? handleCreateBuilding
                    : currentStep === 'cleaning'
                      ? handleSaveCleaningSchedules
                      : handleCreateRoom
                }
                disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    {currentStep === 'room' ? 'Finish Setup' : 'Continue'}
                    {currentStep !== 'room' && <ChevronRight className="w-4 h-4" />}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
